import React from 'react';
import { PlaybookAdherence, getCoveragePctClasses } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';

interface IntroSummaryCardProps {
  playbook?: PlaybookAdherence;
  openerPhaseTimestamp?: string;
  onViewDetails: () => void;
  onViewInTimeline?: () => void;
}

const IntroSummaryCard: React.FC<IntroSummaryCardProps> = ({
  playbook,
  openerPhaseTimestamp,
  onViewDetails,
  onViewInTimeline,
}) => {
  const hasGeneric = !!playbook?.intro_generic;
  const hasPlaybook = playbook?.was_used && playbook?.intro_coverage;
  const coveragePct = playbook?.intro_coverage?.coverage_pct;
  const genericScore = playbook?.intro_generic?.score;

  const displayScore = hasPlaybook && coveragePct != null ? coveragePct : genericScore;

  const badgeColor =
    displayScore == null
      ? 'bg-gray-100 text-gray-500 border border-gray-200'
      : displayScore >= 70
      ? 'bg-green-100 text-green-700 border border-green-200'
      : displayScore >= 40
      ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
      : 'bg-red-100 text-red-700 border border-red-200';

  const statusBadge = displayScore != null ? (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>{displayScore}%</span>
  ) : undefined;

  const coachingText = playbook?.intro_generic?.coaching;

  const missingMsg =
    !playbook
      ? 'Intro analysis requires re-analysis of this session.'
      : !hasGeneric
      ? 'Re-analyze this session to see generic intro analysis.'
      : undefined;

  const resultContent = (
    <div className="space-y-3">
      {hasPlaybook && playbook.intro_coverage && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">Playbook Coverage</p>
            <span className="text-xs font-bold text-blue-800">
              {playbook.intro_coverage.total_delivered}/{playbook.intro_coverage.total_available} points
            </span>
          </div>
          <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${getCoveragePctClasses(playbook.intro_coverage.coverage_pct)}`}
              style={{ width: `${playbook.intro_coverage.coverage_pct}%` }}
            />
          </div>
          {playbook.intro_coverage.intro_quality_summary && (
            <p className="text-xs text-blue-900 mt-2">{playbook.intro_coverage.intro_quality_summary}</p>
          )}
        </div>
      )}
      {playbook?.intro_generic && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Generic Analysis</p>
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${badgeColor}`}>{genericScore}%</span>
          </div>
          <p className="text-xs text-gray-700">{playbook.intro_generic.summary}</p>
          {(playbook.intro_generic.what_worked || playbook.intro_generic.what_missed) && (
            <div className="mt-2 space-y-1 pt-2 border-t border-gray-200">
              {playbook.intro_generic.what_worked && (
                <p className="text-[11px] text-green-700">
                  <span className="font-semibold">Worked: </span>{playbook.intro_generic.what_worked}
                </p>
              )}
              {playbook.intro_generic.what_missed && (
                <p className="text-[11px] text-red-600">
                  <span className="font-semibold">Missed: </span>{playbook.intro_generic.what_missed}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <SummarySectionCard
      title="Intro"
      icon={<span>&#128075;</span>}
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={coachingText ? [coachingText] : undefined}
      sectionKey="intro"
      onViewDetails={onViewDetails}
      onViewInTimeline={openerPhaseTimestamp ? onViewInTimeline : undefined}
      missingDataMessage={missingMsg}
    />
  );
};

export default IntroSummaryCard;
